import { useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';

export interface MobileLayoutConfig {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isSmallMobile: boolean;
  isLargeMobile: boolean;
  breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  spacing: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xxs: number;
  };
  typography: {
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    h5: string;
    h6: string;
    body1: string;
    body2: string;
    button: string;
    caption: string;
  };
}

export const useMobileLayout = (): MobileLayoutConfig => {
  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeMobile = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const breakpoint = useMemo(() => {
    if (isSmallMobile) return 'xs';
    if (isLargeMobile) return 'sm';
    if (isTablet) return 'md';
    if (isDesktop) return 'lg';
    return 'xl';
  }, [isSmallMobile, isLargeMobile, isTablet, isDesktop]);

  const spacing = useMemo(() => ({
    xs: isSmallMobile ? 1 : 2,
    sm: isLargeMobile ? 2 : 3,
    md: isTablet ? 3 : 4,
    lg: isDesktop ? 4 : 5,
    // Add extra small spacing for very small screens
    xxs: isSmallMobile ? 0.5 : 1,
  }), [isSmallMobile, isLargeMobile, isTablet, isDesktop]);

  const typography = useMemo(() => ({
    h1: isSmallMobile ? '1.5rem' : isLargeMobile ? '2rem' : '2.5rem',
    h2: isSmallMobile ? '1.25rem' : isLargeMobile ? '1.5rem' : '2rem',
    h3: isSmallMobile ? '1.125rem' : isLargeMobile ? '1.25rem' : '1.5rem',
    h4: isSmallMobile ? '1rem' : isLargeMobile ? '1.125rem' : '1.25rem',
    h5: isSmallMobile ? '0.875rem' : isLargeMobile ? '1rem' : '1.125rem',
    h6: isSmallMobile ? '0.8rem' : isLargeMobile ? '0.875rem' : '1rem',
    body1: isSmallMobile ? '0.875rem' : '1rem',
    body2: isSmallMobile ? '0.8rem' : '0.875rem',
    button: isSmallMobile ? '0.8rem' : '0.875rem',
    // Add extra small typography for very small screens
    caption: isSmallMobile ? '0.7rem' : '0.75rem',
  }), [isSmallMobile, isLargeMobile]);

  return {
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isLargeMobile,
    breakpoint,
    spacing,
    typography,
  };
};
